import React, { useEffect } from 'react';
import { Menu, X, LogIn, LogOut, ShieldCheck, Calculator, Banknote, Building2, ClipboardList, Home, ChevronRight, FileSearch, Database } from 'lucide-react';
import { TabType, NavbarSettings } from '../types';
import { Logo } from './Logo';

interface NavbarProps {
  activeTab: TabType | 'admin';
  setActiveTab: (tab: TabType | 'admin') => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
  isAdminLoggedIn: boolean;
  setShowLoginModal: (show: boolean) => void;
  setIsAdminLoggedIn: (status: boolean) => void;
  navbarSettings?: NavbarSettings;
}

export const Navbar: React.FC<NavbarProps> = ({ 
  activeTab, 
  setActiveTab, 
  isMenuOpen, 
  setIsMenuOpen,
  isAdminLoggedIn,
  setShowLoginModal,
  setIsAdminLoggedIn,
  navbarSettings = {}
}) => {
  // Disable scrolling when sidebar is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMenuOpen]);

  const items: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'home' as TabType, label: 'Home', icon: <Home size={20} /> },
    { id: 'arrear' as TabType, label: 'AREAR', icon: <Calculator size={20} /> },
    { id: 'salary' as TabType, label: 'सैलरी जानें', icon: <Banknote size={20} /> },
    { id: 'tre2' as TabType, label: 'TDS', icon: <Banknote size={20} /> },
    { id: 'absenty' as TabType, label: 'ABSENTY', icon: <ClipboardList size={20} /> },
    { id: 'brc' as TabType, label: 'BRC', icon: <Building2 size={20} /> },
    { id: 'bpscTre3' as TabType, label: 'ASSET AND LIABILITY', icon: <ClipboardList size={20} /> },
    { id: 'school' as TabType, label: 'SCHOOL', icon: <Building2 size={20} /> },
    { id: 'searchSchool' as TabType, label: 'SEARCH YOUR SCHOOL', icon: <FileSearch size={20} /> },
    { id: 'dataManager' as TabType, label: 'DATA MANAGER', icon: <Database size={20} /> },
  ].filter(item => navbarSettings[item.id] !== false);

  return (
    <>
      <nav className="bg-gradient-to-r from-orange-600 to-red-700 text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 md:h-20 flex justify-between items-center">
          <div className="flex items-center space-x-2 md:space-x-3 cursor-pointer" onClick={() => setActiveTab('home')}>
            <Logo size={36} className="md:w-12 md:h-12 drop-shadow-md" />
            <div>
              <h1 className="text-base md:text-lg font-black leading-none tracking-tight">BR TEACHER</h1>
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            {items.map(item => (
              <button 
                key={item.id} 
                onClick={() => setActiveTab(item.id)} 
                className={`text-sm font-bold transition-all px-3 py-2 rounded-lg hover:bg-white/10 ${activeTab === item.id ? 'text-yellow-300' : 'text-white hover:text-orange-100'}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <button 
            className="p-1.5 md:p-2 hover:bg-white/10 rounded-lg md:rounded-xl transition-all lg:hidden" 
            onClick={() => setIsMenuOpen(true)}
            aria-label="Open Menu"
          >
            <Menu size={24} className="md:w-8 md:h-8" />
          </button>
        </div>
      </nav>

      {/* Mobile Sidebar Overlay */}
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] transition-opacity duration-300 md:hidden ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsMenuOpen(false)}
      />

      {/* Mobile Sidebar Panel */}
      <aside 
        className={`fixed top-0 right-0 w-[85%] max-w-sm h-full bg-white z-[70] shadow-2xl transition-transform duration-500 ease-in-out lg:hidden ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-4 md:p-6 bg-gradient-to-br from-orange-600 to-red-700 text-white flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Logo size={32} className="md:w-10 md:h-10 bg-white rounded-full p-1" />
              <div className="font-black text-xs md:text-sm tracking-tight">TEACHER PORTAL</div>
            </div>
            <button 
              onClick={() => setIsMenuOpen(false)}
              className="p-1.5 md:p-2 bg-white/20 hover:bg-white/30 rounded-full transition-all"
            >
              <X size={20} className="md:w-6 md:h-6" />
            </button>
          </div>

          {/* Sidebar Nav Items */}
          <div className="flex-grow overflow-y-auto p-3 md:p-4 space-y-1.5 md:space-y-2 no-scrollbar bg-gray-50/50">
            <p className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-3 md:mb-4 ml-3 md:ml-4">Main Menu</p>
            {items.map(item => (
              <button 
                key={item.id} 
                onClick={() => {setActiveTab(item.id); setIsMenuOpen(false)}} 
                className={`w-full group flex items-center justify-between p-3 md:p-4 rounded-xl md:rounded-2xl transition-all ${activeTab === item.id ? 'bg-orange-600 text-white shadow-xl shadow-orange-100' : 'hover:bg-white hover:shadow-md text-gray-700'}`}
              >
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className={`p-1.5 md:p-2 rounded-lg md:rounded-xl transition-colors ${activeTab === item.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-orange-50 group-hover:text-orange-600'}`}>
                    {React.cloneElement(item.icon as React.ReactElement<any>, { size: 18 })}
                  </div>
                  <span className="font-bold text-sm md:text-base tracking-tight">{item.label}</span>
                </div>
                <ChevronRight size={16} className={`opacity-40 transition-transform ${activeTab === item.id ? 'translate-x-1 opacity-100' : 'group-hover:translate-x-1'}`} />
              </button>
            ))}

            <div className="h-[1px] bg-gray-200 my-4 md:my-6 mx-3 md:mx-4"></div>
            
            <p className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-3 md:mb-4 ml-3 md:ml-4">Account & Access</p>
            {!isAdminLoggedIn ? (
              <button 
                onClick={() => {setShowLoginModal(true); setIsMenuOpen(false)}} 
                className="w-full flex items-center p-3 md:p-4 rounded-xl md:rounded-2xl bg-slate-900 text-white shadow-lg hover:bg-black transition-all"
              >
                <div className="p-1.5 md:p-2 bg-white/10 rounded-lg md:rounded-xl mr-3 md:mr-4">
                  <LogIn size={18} className="text-orange-400" />
                </div>
                <span className="font-bold text-sm md:text-base">Admin Login</span>
              </button>
            ) : (
              <div className="space-y-1.5 md:space-y-2">
                <button 
                  onClick={() => {setActiveTab('admin'); setIsMenuOpen(false)}} 
                  className={`w-full flex items-center p-3 md:p-4 rounded-xl md:rounded-2xl transition-all ${activeTab === 'admin' ? 'bg-green-600 text-white shadow-lg' : 'bg-green-50 text-green-700'}`}
                >
                  <div className={`p-1.5 md:p-2 rounded-lg md:rounded-xl mr-3 md:mr-4 ${activeTab === 'admin' ? 'bg-white/20' : 'bg-green-100'}`}>
                    <ShieldCheck size={18} />
                  </div>
                  <span className="font-bold text-sm md:text-base">Dashboard</span>
                </button>
                <button 
                  onClick={() => {setIsAdminLoggedIn(false); setIsMenuOpen(false)}} 
                  className="w-full flex items-center p-3 md:p-4 rounded-xl md:rounded-2xl text-red-600 bg-red-50 hover:bg-red-100 transition-all font-bold"
                >
                  <div className="p-1.5 md:p-2 bg-red-100 rounded-lg md:rounded-xl mr-3 md:mr-4">
                    <LogOut size={18} />
                  </div>
                  <span className="text-sm md:text-base">Logout Session</span>
                </button>
              </div>
            )}
          </div>

          {/* Sidebar Footer */}
          <div className="p-6 border-t border-gray-100 bg-white">
            <div className="flex items-center space-x-3 opacity-60">
              <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600">
                <ShieldCheck size={16} />
              </div>
              <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Secure Portal v1.0</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};